import React, { useCallback, useRef, useEffect, useState } from "react";
import { Box, Checkbox, getColor, Radio, Toggle, Typography } from "fds";
import "./index.scss";
import { permissionDTO } from "../../types/auth";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import { Workgroup } from "../../types/workgroup";
import { t } from "i18next";

interface WorkgroupAlarmTabProps {
  policy?: permissionDTO;
  changeWorkgroupPolicy: (newPolicy: permissionDTO) => void;
  workgroupInfo?: Workgroup;
  isWorkgroupManager?: boolean;
}
export const file_alert_type = [
  { key: "fileshare", name: t("header.setAlarm.shareFile") },
  { key: "revision", name: t("header.setAlarm.revision") },
  { key: "memo", name: t("header.setAlarm.comment") },
];
const read_request_type = [
  { key: "readRequest", name: t("header.setAlarm.requestRecieve") },
  { key: "readRequestConfirm", name: t("header.setAlarm.confirmRequest") },
];
const WorkgroupAlarmTab: React.FC<WorkgroupAlarmTabProps> = ({
  policy,
  workgroupInfo,
  isWorkgroupManager,
  changeWorkgroupPolicy,
}) => {
  /** hook 정리 */
  const { getWorkgroupNoti } = useWorkgroupStore();

  /** UseEffect정리 */
  useEffect(() => {
    if (!workgroupInfo) return;
    getWorkgroupNoti(workgroupInfo?.workgroupId);
  }, [workgroupInfo]);

  /** 함수 정리 */
  /** UI: 멤버일 경우, 알림 여부 표시 */
  const renderNotificationPolicyToSTr = (type: string) => {
    switch (type) {
      case "workgroup_invite":
        return policy?.policy?.notificationPolicy?.usingWorkgroupInvitedAlert
          ? "ON"
          : "OFF";
      case "file_update":
        const returnStr = file_alert_type.filter((item) =>
          policy?.policy?.notificationPolicy?.fileAlert.includes(item.key)
        );
        if (
          returnStr.length === 0 ||
          !policy?.policy?.notificationPolicy?.usingFileAlert
        ) {
          return "OFF";
        } else {
          return "ON: " + returnStr.map((item) => item.name).join(", ");
        }
      case "read_request":
        const returnStr1 = read_request_type.filter((item) =>
          policy?.policy?.notificationPolicy?.readRequestAlert?.includes(
            item.key
          )
        );
        if (
          returnStr1.length === 0 ||
          !policy?.policy?.notificationPolicy?.usingReadRequestAlert
        ) {
          return "OFF";
        } else {
          return "ON: " + returnStr1.map((item) => item.name).join(", ");
        }
    }
  };
  /** UI: 워크그룹 알람 설정 - 일반 사용자 화면 */
  const renderUserMode = () => {
    return (
      <Box direction="column">
        <Typography type="subtitle2">{t("header.alarm.title")}</Typography>
        <div className="workgroupModal-body--header" />
        <Box classes="mg mt-20 workgroupModal-body--items">
          <Box>
            <Typography
              type="subtitle2"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              width={120}
            >
              {t("header.setAlarm.inviteWorkgroup")}
            </Typography>
            <Typography
              type="body2_rg"
              classes="mg ml-10"
              maxWidth={"calc(100% - 130px)"}
              exactColor={getColor("COLOR_TEXT_PRIMARY")}
            >
              {renderNotificationPolicyToSTr("workgroup_invite")}
            </Typography>
          </Box>
          <Box>
            <Typography
              type="subtitle2"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              width={120}
            >
              {t("header.setAlarm.fileUpdate")}
            </Typography>
            <Typography
              type="body2_rg"
              classes="mg ml-10"
              maxWidth={"calc(100% - 130px)"}
              exactColor={getColor("COLOR_TEXT_PRIMARY")}
            >
              {renderNotificationPolicyToSTr("file_update")}
            </Typography>
          </Box>{" "}
          <Box>
            <Typography
              type="subtitle2"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              width={120}
            >
              {t("header.setAlarm.request")}
            </Typography>
            <Typography
              type="body2_rg"
              classes="mg ml-10"
              maxWidth={"calc(100% - 130px)"}
              exactColor={getColor("COLOR_TEXT_PRIMARY")}
            >
              {renderNotificationPolicyToSTr("read_request")}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };
  /** 워크그룹 알람 설정 */
  const handleChangeAlarmInfo = (
    target: string,
    value?: string | string[] | boolean
  ) => {
    if (value === undefined) return;
    policy?.policy?.notificationPolicy &&
      changeWorkgroupPolicy({
        ...policy,
        policy: {
          ...policy.policy,
          notificationPolicy: {
            ...policy.policy.notificationPolicy,
            fileAlert:
              target === "usingFileAlert"
                ? []
                : policy.policy.notificationPolicy.fileAlert,
            readRequestAlert:
              target === "usingReadRequestAlert"
                ? []
                : policy.policy.notificationPolicy.readRequestAlert,

            [target]: value,
          },
        },
      });
  };
  /** UI: 워크그룹 알람 설정 - 워크그룹장 화면 */
  const renderMasterMode = () => {
    return (
      <Box direction="column">
        <Box alignItems="center">
          <Box direction="column">
            <Typography type="subtitle2">
              {t("header.setAlarm.inviteWorkgroup")}
            </Typography>
            <Typography
              type="body4_rg"
              width={418}
              classes="mg mt-4"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("admin.config.alarm.workgroupInfo")}
            </Typography>
          </Box>
          <Toggle
            classes="mg ml-auto"
            checked={
              policy?.policy?.notificationPolicy?.usingWorkgroupInvitedAlert
            }
            onChange={() =>
              handleChangeAlarmInfo(
                "usingWorkgroupInvitedAlert",
                !policy?.policy?.notificationPolicy?.usingWorkgroupInvitedAlert
              )
            }
          />
        </Box>
        <Box alignItems="center" classes="mg mt-32">
          <Box direction="column">
            <Typography type="subtitle2">
              {t("header.setAlarm.fileUpdate")}
            </Typography>
            <Typography
              type="body4_rg"
              classes="mg mt-4"
              width={418}
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("admin.config.alarm.fileInfo")}
            </Typography>
          </Box>
          <Toggle
            classes="mg ml-auto"
            checked={policy?.policy?.notificationPolicy?.usingFileAlert}
            onChange={() =>
              handleChangeAlarmInfo(
                "usingFileAlert",
                !policy?.policy?.notificationPolicy?.usingFileAlert
              )
            }
          />
        </Box>
        {policy?.policy?.notificationPolicy?.usingFileAlert && (
          <Box direction="column" alignItems="flex-start" classes="mg mt-20">
            {file_alert_type.map((item, index) => (
              <Checkbox
                label={item.name}
                classDiv={index > 0 ? "mg mt-20" : ""}
                check={policy?.policy?.notificationPolicy?.fileAlert.includes(
                  item.key
                )}
                onClick={() =>
                  handleChangeAlarmInfo(
                    "fileAlert",
                    policy?.policy?.notificationPolicy?.fileAlert.includes(
                      item.key
                    )
                      ? policy?.policy?.notificationPolicy.fileAlert.filter(
                          (item1) => item1 !== item.key
                        )
                      : policy?.policy?.notificationPolicy
                      ? [
                          ...policy?.policy?.notificationPolicy?.fileAlert,
                          item.key,
                        ]
                      : [item.key]
                  )
                }
              />
            ))}
          </Box>
        )}
        <Box alignItems="center" classes="mg mt-32">
          <Box direction="column">
            <Typography type="subtitle2">
              {t("header.setAlarm.request")}
            </Typography>
            <Typography
              type="body4_rg"
              width={418}
              classes="mg mt-4"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("admin.config.alarm.readReqestInfo")}
            </Typography>
          </Box>
          <Toggle
            classes="mg ml-auto"
            checked={policy?.policy?.notificationPolicy?.usingReadRequestAlert}
            onChange={() =>
              handleChangeAlarmInfo(
                "usingReadRequestAlert",
                !policy?.policy?.notificationPolicy?.usingReadRequestAlert
              )
            }
          />
        </Box>
        {policy?.policy?.notificationPolicy?.usingReadRequestAlert && (
          <Box direction="column" alignItems="flex-start" classes="mg mt-20">
            {read_request_type.map((item, index) => (
              <Checkbox
                label={item.name}
                classDiv={index > 0 ? "mg mt-20" : ""}
                check={policy?.policy?.notificationPolicy?.readRequestAlert.includes(
                  item.key
                )}
                onClick={() =>
                  handleChangeAlarmInfo(
                    "readRequestAlert",
                    policy?.policy?.notificationPolicy?.readRequestAlert.includes(
                      item.key
                    )
                      ? policy?.policy?.notificationPolicy.readRequestAlert.filter(
                          (item1) => item1 !== item.key
                        )
                      : policy?.policy?.notificationPolicy
                      ? [
                          ...policy?.policy?.notificationPolicy
                            ?.readRequestAlert,
                          item.key,
                        ]
                      : [item.key]
                  )
                }
              />
            ))}
          </Box>
        )}
      </Box>
    );
  };
  return isWorkgroupManager ? renderMasterMode() : renderUserMode();
};
export default WorkgroupAlarmTab;
